import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate, useSearchParams } from "react-router-dom"
import { auth } from "@/firebase/firebase"
import {
   confirmPasswordReset,
   signInWithEmailAndPassword,
   updatePassword,
   verifyPasswordResetCode,
} from "firebase/auth"
import { useProcess } from "@/contexts/ProcessContext"
import { useEffect, useState } from "react"
import { FirebaseError } from "firebase/app"

const passwordSchema = z
   .object({
      password: z
         .string()
         .min(8, "Le mot de passe doit contenir au moins 8 caractères")
         .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
         .regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
         .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre"),
      confirmPassword: z.string(),
   })
   .refine((data) => data.password === data.confirmPassword, {
      message: "Les mots de passe ne correspondent pas",
      path: ["confirmPassword"],
   })

type FormValues = z.infer<typeof passwordSchema>

export default function AuthAction() {
   const [showNewPassword, setShowNewPassword] = useState(false)
   const [showConfirmPassword, setShowConfirmPassword] = useState(false)
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const { startProcess } = useProcess()
   const [email, setEmail] = useState<string>()

   const mode = searchParams.get("mode")
   const oobCode = searchParams.get("oobCode")

   // invitation process
   const emailParam = searchParams.get("email")
   const passwordParam = searchParams.get("code")
   const continueUrl = searchParams.get("continueUrl")
   const isInvitation = mode === "invitation"

   const form = useForm<FormValues>({
      resolver: zodResolver(passwordSchema),
   })

   useEffect(() => {
      if (mode === "resetPassword") {
         startProcess(async () => {
            if (!oobCode) throw new Error("Code de réinitialisation manquant")
            const email = await verifyPasswordResetCode(auth, oobCode)
            setEmail(email)
         })
      }
   }, [oobCode])

   useEffect(() => {
      if (emailParam) {
         setEmail(emailParam)
      }
   }, [emailParam])

   if (mode !== "resetPassword" && !isInvitation) {
      return (
         <div className="hero min-h-screen">
            <div className="hero-content text-center">
               <div className="max-w-md">
                  <h1 className="text-2xl font-bold mb-4">Non implémenté</h1>
                  <p>Cette action n'est pas encore disponible.</p>
               </div>
            </div>
         </div>
      )
   }

   const onSubmit = async (data: FormValues) => {
      startProcess(async () => {
         if (mode === "resetPassword") {
            if (!oobCode) throw new Error("Code de réinitialisation manquant")
            await confirmPasswordReset(auth, oobCode, data.password)
            await signInWithEmailAndPassword(auth, email || "", data.password)
         } else if (isInvitation) {
            if (!emailParam) throw new Error("Paramètre email manquant")
            if (!passwordParam) throw new Error("Paramètre mot de passe manquant")
            try {
               const userCredential = await signInWithEmailAndPassword(
                  auth,
                  emailParam || "",
                  passwordParam || "",
               )

               await updatePassword(userCredential.user, data.password)
            } catch (error) {
               if (error instanceof FirebaseError && error.code === "auth/invalid-credential") {
                  throw new Error(
                     "Le lien d'initialisation de votre mot de passe n'est plus valable.\n Veuillez réinitialiser votre mot de passe:\n - Allez sur 'Connexion'\n - Cliquez sur 'Mot de passe oublé'.",
                  )
               }
               throw error
            }
         }

         navigate(continueUrl || "/")
      })
   }

   return (
      <div className="hero _min-h-screen">
         <div className="hero-content flex-col">
            <h1 className="mt-8 text-2xl font-bold mb-4">
               {isInvitation
                  ? "Bienvenue sur l'app Télà? ! Commencez par indiquer votre mot de passe"
                  : "Vous pouvez maintenant indiquer votre nouveau mot de passe"}
            </h1>

            <div className="text-center lg:text-left mb-8">
               <p className="mb-4">Le mot de passe doit avoir au moins </p>
               <ul className="list-disc list-inside space-y-2">
                  <li>8 caractères</li>
                  <li>Une majuscule</li>
                  <li>Une minuscule</li>
                  <li>Un chiffre</li>
               </ul>
            </div>

            <div className="card shrink-0 w-full max-w-sm shadow-2xl bg-base-100">
               <form className="card-body" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="form-control">
                     <label className="label">
                        <span className="label-text">Email</span>
                     </label>
                     <input
                        id="username"
                        name="username"
                        type="email"
                        className="input input-bordered"
                        value={email || ""}
                        disabled
                        autoComplete="username"
                     />
                  </div>
                  <div className="form-control">
                     <label className="label">
                        <span className="label-text">
                           {isInvitation ? "Mot de passe" : "Nouveau mot de passe"}
                        </span>
                     </label>
                     <div className="relative">
                        <input
                           id="new-password"
                           type={showNewPassword ? "text" : "password"}
                           className="input input-bordered w-full pr-10"
                           {...form.register("password")}
                           autoComplete="new-password"
                        />
                        <button
                           type="button"
                           className="absolute inset-y-0 right-0 flex items-center px-2"
                           onClick={() => setShowNewPassword((prev: boolean) => !prev)}
                           aria-label={showNewPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        >
                           {!showNewPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-.656 2.11-2.197 3.973-4.542 5.001M15.54 17.44A9.978 9.978 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.978 9.978 0 012.042-3.001" />
                              </svg>
                           ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.978 9.978 0 012.042-3.001m3.1-3.1A9.978 9.978 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.978 9.978 0 01-4.043 5.001M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                              </svg>
                           )}
                        </button>
                     </div>
                     {form.formState.errors.password && (
                        <label className="label">
                           <span className="label-text-alt text-error">
                              {form.formState.errors.password.message}
                           </span>
                        </label>
                     )}
                  </div>
                  <div className="form-control">
                     <label className="label">
                        <span className="label-text">Confirmer le mot de passe</span>
                     </label>
                     <div className="relative">
                        <input
                           id="confirm-new-password"
                           type={showConfirmPassword ? "text" : "password"}
                           className="input input-bordered w-full pr-10"
                           {...form.register("confirmPassword")}
                           autoComplete="new-password"
                        />
                        <button
                           type="button"
                           className="absolute inset-y-0 right-0 flex items-center px-2"
                           onClick={() => setShowConfirmPassword((prev: boolean) => !prev)}
                           aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        >
                           {!showConfirmPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-.656 2.11-2.197 3.973-4.542 5.001M15.54 17.44A9.978 9.978 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.978 9.978 0 012.042-3.001" />
                              </svg>
                           ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.978 9.978 0 012.042-3.001m3.1-3.1A9.978 9.978 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.978 9.978 0 01-4.043 5.001M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                              </svg>
                           )}
                        </button>
                     </div>
                     {form.formState.errors.confirmPassword && (
                        <label className="label">
                           <span className="label-text-alt text-error">
                              {form.formState.errors.confirmPassword.message}
                           </span>
                        </label>
                     )}
                  </div>
                  <div className="form-control mt-6">
                     <button type="submit" className="btn btn-primary">
                        Valider
                     </button>
                  </div>
               </form>
            </div>
         </div>
      </div>
   )
}
